package one.healthium.health_log.ui

import co.touchlab.kermit.Logger
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.update
import one.healthium.domain.health_log.HealthLogInteractor
import one.healthium.domain.health_log.models.HealthLogAnsModel
import one.healthium.domain.health_log.models.HealthLogAnswerModel
import one.healthium.domain.health_log.models.HealthLogAnswerOption
import one.healthium.domain.health_log.models.HealthLogOption
import one.healthium.health_log.ui.models.HealthLogQuestionUiModel
import one.healthium.ui.common.event_producer.EventProducer
import one.healthium.ui.common.message.UiMessage
import one.healthium.ui.common.message.UiMessageManager
import one.healthium.ui.common.vm.BaseViewModel

internal class HealthLogViewModel(
    private val healthLogInteractor: HealthLogInteractor,
) : BaseViewModel() {

    private val _state = MutableStateFlow(HealthLogUiState())
    val state = _state.asStateFlow()

    val uiMessage = UiMessageManager()
    val events = EventProducer<HealthLogEvent>()

    init {
        launch {
            _state.update { currentState ->
                currentState.copy(loading = true)
            }
            try {
                val healthLogQuestions = healthLogInteractor.getHealthLogQuestions()
                _state.update { currentState ->
                    currentState.copy(
                        questions = healthLogQuestions
                    )
                }
            } catch (e: Exception) {
                Logger.e(e) { "Error fetching health log questions" }
            }
            _state.update { currentState ->
                currentState.copy(loading = false)
            }
        }
        healthLogInteractor.getTodayHealthLog()
            .onEach { todayHealthLog ->
                _state.update { currentState ->
                    currentState.copy(todayHealthLog = todayHealthLog)
                }
            }
            .catch {
                Logger.e(it) { "Error fetching today's health log" }
            }
            .launch()
    }

    fun onOptionSelected(question: HealthLogQuestionUiModel, option: HealthLogAnswerModel) {
        _state.update { currentState ->
            val updatedSelectedAnswers = if (question.selectedOption == option) {
                currentState.selectedAnswers + (question.id to null)
            } else {
                currentState.selectedAnswers + (question.id to option)
            }
            currentState.copy(selectedAnswers = updatedSelectedAnswers)
        }
    }

    fun onSaveClicked(taskId: String?) {
        launch {
            try {
                val userAnswers = _state.value.selectedAnswers.toMutableMap()
                if (userAnswers.isNotEmpty()) { // user changed something, need to save
                    val updatedHealthLogOptions = mutableListOf<HealthLogOption>()

                    _state.value.todayHealthLog?.answers
                        ?.forEach { answer ->
                            val option = if (userAnswers.containsKey(answer.questionId)) {
                                val userAnswer = userAnswers.remove(answer.questionId)
                                HealthLogOption(
                                    questionId = answer.questionId,
                                    answers = if (userAnswer != null) {
                                        listOf(
                                            HealthLogAnswerOption(
                                                answerId = userAnswer.id,
                                                weight = userAnswer.weight
                                            )
                                        )
                                    } else emptyList()
                                )
                            } else {
                                HealthLogOption(
                                    questionId = answer.questionId,
                                    answers = answer.answers.toOptions(),
                                )
                            }
                            updatedHealthLogOptions.add(option)
                        }

                    if (userAnswers.isNotEmpty()) {
                        // if there are still answers left, add them as new entries
                        userAnswers.forEach { (questionId, answer) ->
                            updatedHealthLogOptions.add(
                                HealthLogOption(
                                    questionId = questionId,
                                    answers = if (answer != null) {
                                        listOf(
                                            HealthLogAnswerOption(
                                                answerId = answer.id,
                                                weight = answer.weight
                                            )
                                        )
                                    } else emptyList()
                                )
                            )
                        }
                    }

                    _state.update { currentState ->
                        currentState.copy(saveInProgress = true)
                    }
                    healthLogInteractor.addHealthLog(updatedHealthLogOptions, taskId)
                }
                events.trigger(HealthLogEvent.OnHealthLogSaved)
            } catch (e: Exception) {
                Logger.e(e) { "Error saving health log" }
                uiMessage.add(UiMessage.throwable(e))
            } finally {
                _state.update { currentState ->
                    currentState.copy(saveInProgress = false)
                }
            }
        }
    }

    private fun List<HealthLogAnsModel>.toOptions(): List<HealthLogAnswerOption> {
        return this.map { ansModel ->
            HealthLogAnswerOption(
                answerId = ansModel.answerId,
                weight = ansModel.weight,
            )
        }
    }
}